import React, { useRef } from 'react';
import { ReportFileFiltersType } from '../hooks/useTmsReportFileQuery';
import InputFilterItem from '../../../delivery/components/InputFilterItem';
import ParcelFilterStyle from '../../../delivery/components/ParcelFilterStyle';
import DateRangeFilterItem from '../../../delivery/components/DateRangeFilterItem';
import { useTranslation } from 'react-i18next';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import { getLastWeekDateRange } from '../../../../utils/dateTimeUtils';
import { useAutoClearDefaultTime } from '../../../../hooks/useAutoClearDefaultTime';
import { ReportFileTypeEnum } from '../../../../enums/ReportFileEnum';

interface TmsReportFileFilterProps {
    filters: ReportFileFiltersType;
    setFilters: (filters: ReportFileFiltersType) => void;
}

const TmsReportFileFilter: React.FC<TmsReportFileFilterProps> = ({
    filters,
    setFilters,
}) => {
    const { t } = useTranslation();
    const hasUserEditedReportDateRef = useRef(false);
    useAutoClearDefaultTime({
        filters,
        setFilters,
        timeKey: 'reportDate',
        hasUserEditedTimeRef: hasUserEditedReportDateRef,
        skipFirstNonTimeChange: true,
    });

    const getEffectiveReportDate = (reportDate: unknown): [string | null, string | null] | null => {
        if (reportDate === null) return null;
        const isValid = reportDate &&
            Array.isArray(reportDate) &&
            reportDate.length === 2;
        return isValid ? (reportDate as [string | null, string | null]) : getLastWeekDateRange();
    };

    const reportTypeOptions = [
        { value: '', label: t('enum.reportTypeOptions.all') },
        { value: String(ReportFileTypeEnum.TMS_TASK), label: t('enum.reportTypeOptions.tmsTaskDownload') },
    ];

    const handleTypeChange = (value: string) => {
        setFilters({ ...filters, type: value ? parseInt(value, 10) : undefined });
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilters({ ...filters, name: e.target.value || undefined });
    };

    const handleReportDateChange = (dates: [string | null, string | null] | null) => {
        hasUserEditedReportDateRef.current = true;
        setFilters({ ...filters, reportDate: dates });
    };

    const col1Labels = [t('central.download.filters.reportDate')];
    const col2Labels = [t('central.download.filters.reportName')];
    const col3Labels = [t('central.download.filters.type')];
    const col1SpanClassName = getSmartLabelClassName(col1Labels);
    const col2SpanClassName = getSmartLabelClassName(col2Labels);
    const col3SpanClassName = getSmartLabelClassName(col3Labels);

    return (
        <div className="mb-2 py-2">
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                <div className="min-w-0">
                    <DateRangeFilterItem
                        label={t('central.download.filters.reportDate')}
                        value={getEffectiveReportDate(filters.reportDate)}
                        onChange={handleReportDateChange}
                        spanClassName={col1SpanClassName}
                    />
                </div>
                <div className="min-w-0">
                    <InputFilterItem
                        label={t('central.download.filters.reportName')}
                        value={filters.name?.toString() || ''}
                        onChange={handleNameChange}
                        spanClassName={col2SpanClassName}
                    />
                </div>
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={t('central.download.filters.type')}
                        value={filters.type !== undefined ? filters.type.toString() : ''}
                        onChange={handleTypeChange}
                        options={reportTypeOptions}
                        spanClassName={col3SpanClassName}
                        className="w-full"
                    />
                </div>
            </div>
        </div>
    );
};

export default TmsReportFileFilter;
